"use client";

import { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import ActionButton from "../ActionButton";
import CallBack from "../popup";

type Agency = {
  id?: number | string;
  nom?: string;
  telephone?: string;
  slug?: string;
};

const AGENCY_ROUTE_PREFIX = "/livraison-repas-entreprise/";

export default function ActionSidebar() {
  const pathname = usePathname();
  const [showPopup, setShowPopup] = useState(false);
  const [agency, setAgency] = useState<Agency | null>(null);
  const [loadingAgency, setLoadingAgency] = useState(false);

  const isAgencyPage = useMemo(() => {
    return pathname.startsWith(AGENCY_ROUTE_PREFIX);
  }, [pathname]);

  const agencySlug = useMemo(() => {
    if (!isAgencyPage) return null;
    return pathname.replace(AGENCY_ROUTE_PREFIX, "").split("/")[0] || null;
  }, [pathname, isAgencyPage]);

  useEffect(() => {
    const fetchAgency = async () => {
      if (!isAgencyPage || !agencySlug) {
        setAgency(null);
        return;
      }

      const endpoint = process.env.NEXT_PUBLIC_STRAPI_OUR_AGENCIES_ENDPOINT;

      if (!endpoint) {
        console.error("NEXT_PUBLIC_STRAPI_OUR_AGENCIES_ENDPOINT is not defined");
        setAgency(null);
        return;
      }

      try {
        setLoadingAgency(true);

        const url = new URL(endpoint);
        url.searchParams.set("filters[slug][$eq]", agencySlug);
        url.searchParams.set("populate", "*");

        const res = await fetch(url.toString(), {
          method: "GET",
          cache: "no-store",
        });

        if (!res.ok) {
          throw new Error("Failed to fetch agency");
        }

        const json = await res.json();
        const item = json?.data?.[0] || null;

        if (!item) {
          setAgency(null);
          return;
        }

        setAgency({
          id: item.id,
          nom: item.nom,
          telephone: item.telephone,
          slug: item.slug,
        });
      } catch (error) {
        console.error("Erreur récupération agence :", error);
        setAgency(null);
      } finally {
        setLoadingAgency(false);
      }
    };

    fetchAgency();
  }, [isAgencyPage, agencySlug]);

  const canShowCallback =
    isAgencyPage && !loadingAgency && !!agency?.telephone;

  return (
    <>
      {/* Desktop */}
      <aside
        aria-label="Actions rapides"
        className="fixed right-4 top-1/2 z-50 hidden -translate-y-1/2 flex-col gap-4 lg:flex"
      >
        {canShowCallback && (
          <ActionButton
            iconName="/phone.svg"
            label="ÊTRE RAPPELÉ"
            onClick={() => setShowPopup(true)}
            className="min-w-[220px] shadow-[0_10px_30px_rgba(221,34,96,0.35)]"
          />
        )}

        <ActionButton
          iconName="/Commander.svg"
          label="PASSER COMMANDE"
          href={process.env.NEXT_PUBLIC_STRAPI_COMMANDE_ENDPOINT || "#"}
          className="min-w-[220px] shadow-[0_10px_30px_rgba(221,34,96,0.35)]"
        />
      </aside>

      {/* Mobile */}
      <div
        aria-label="Actions rapides"
        className="fixed inset-x-0 bottom-0 z-50 bg-pink py-1 lg:hidden"
      >        <div className="flex  justify-center items-stretch gap-3 px-4">
          <ActionButton
            iconName="/Commander.svg"
            label="PASSER COMMANDE"
            href={process.env.NEXT_PUBLIC_STRAPI_COMMANDE_ENDPOINT || "#"}
            className="justify-center shadow-[0_10px_30px_rgba(221,34,96,0.35)]"
          />

          {canShowCallback && (
            <ActionButton
              iconName="/phone.svg"
              label="ÊTRE RAPPELÉ"
              onClick={() => setShowPopup(true)}
              className="justify-center shadow-[0_10px_30px_rgba(221,34,96,0.35)]"
            />
          )}
        </div>
      </div>

      {showPopup && agency && (
        <CallBack
          agency={agency}
          onClose={() => setShowPopup(false)}
        />
      )}
    </>
  );
}